export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export const MOBILE_PATTERN = /^[6-9]\d{9}$/;
export const EMAIL_PATTERN = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
export const UDYAM_PATTERN = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;
export const FILE_SIZE_LIMIT = 5 * 1024 * 1024; 
export const VALID_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
export const SALES_AMOUNT_PATTERN = /^\d+$/;

export const isRemoteFile = (file) => {
  return file && file.remote === true && file.url;
};

export const validateFileSize = (file) => {
  if (!file) return true;
  if (isRemoteFile(file)) return true;
  return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
};

export const validateFileType = (file) => {
  if (!file) return true;
  if (isRemoteFile(file)) return true;
  return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
};

export const basicDetailsValidation = {
  gstin: {
    pattern: {
      value: GSTIN_PATTERN,
      message: "Please enter a valid 15-digit GSTIN number"
    },
    validate: {
      validGstin: (value) => {
        if (value && !GSTIN_PATTERN.test(value)) {
          return "Please enter a valid 15-digit GSTIN number";
        }
        return true;
      }
    }
  },
  industry: {
    required: "Please select an industry"
  },
  sector: {
    required: "Please select a sector"
  },
  otherSector: {
    required: "Please Enter a sector"
  },
  mobileNumber: {
    required: "Mobile number is required",
    pattern: {
      value: MOBILE_PATTERN,
      message: "Please enter a valid 10-digit mobile number"
    }
  },
  namePerGstin: {
    validate: {
      validName: (value) => {
        if (value && value.trim() === '') {
          return "Name cannot be empty if provided";
        }
        return true;
      }
    }
  },
  address: {
    validate: {
      validAddress: (value) => {
        if (value && value.trim() === '') {
          return "Address cannot be empty if provided";
        }
        return true;
      }
    }
  },
  confirmations: {
    required: "You must accept the terms and conditions"
  }
};

export const entityDocUploadValidation = {
  email: {
    required: "Business email is required",
    pattern: {
      value: EMAIL_PATTERN,
      message: "Please enter a valid email address"
    },
    validate: {
      validEmail: (value) => {
        if (!EMAIL_PATTERN.test(value)) {
          return "Please enter a valid email address";
        }
        return true;
      }
    }
  },
  udyamRegistrationNo: {
    pattern: {
      value: UDYAM_PATTERN,
      message: "Please enter a valid Udyam registration number (Format: UDYAM-XX-00-0000000)"
    },
    validate: {
      validUdyam: (value) => {
        if (value && !UDYAM_PATTERN.test(value)) {
          return "Please enter a valid Udyam registration number";
        }
        return true;
      }
    }
  }
};

export const entityDocUploadCompanyValidation = {
  company: {
    required: "Company documents are required",
    validate: {
      fileSize: (file) => {
        if (!file) return "Company documents are required";
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return "Company documents are required";
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  directorsEquityStructure: {
    required: "Director documents are required",
    validate: {
      fileSize: (file) => {
        if (!file) return "Director documents are required";
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return "Director documents are required";
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  annualFinancials: {
    required: "Financial documents are required",
    validate: {
      fileSize: (file) => {
        if (!file) return "Financial documents are required";
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return "Financial documents are required";
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  totalSalesCurrentYear: {
    required: {
      value: true,
      message: "Year-to-date sales is required"
    },
    pattern: {
      value: /^\d+$/,
      message: "Please enter numbers only"
    },
    validate: {
      required: (value) => {
        if (!value) {
          return "Year-to-date sales is required";
        }
        if (typeof value === 'string' && value.trim() === '') {
          return "Year-to-date sales is required";
        }
        return true;
      },
      positive: (value) => {
        if (value && parseInt(value) <= 0) {
          return "Amount must be greater than 0";
        }
        return true;
      },
      maxLength: (value) => {
        if (value && value.length > 14) {
          return "Amount is too large";
        }
        return true;
      },
      validNumber: (value) => {
        if (value && !/^\d+$/.test(value)) {
          return "Please enter a valid number";
        }
        return true;
      }
    }
  },
  partnershipList: {
    required: "Partnership agreement & partners list is required",
    validate: {
      fileSize: (file) => {
        if (!file) return "Partnership agreement & partners list is required";
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return "Partnership agreement & partners list is required";
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  companyPan: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  companyMoa: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  companyAoa: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  directorsList: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  shareholdingPattern: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  itrFY: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  auditedBalanceFYPrev1: {
    required: "Audited balance sheet is required",
    validate: {
      fileSize: (file) => {
        if (!file) return "Audited balance sheet is required";
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return "Audited balance sheet is required";
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
    auditedBalanceFYPrev2: {
    required: "Audited balance sheet is required",
    validate: {
      fileSize: (file) => {
        if (!file) return "Audited balance sheet is required";
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return "Audited balance sheet is required";
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
};

export const entityBankDocumentsValidation = {
  bankDetail: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  slLoanDetails: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  bankStatementOptional: {
    validate: {
      fileSize: (file) => {
        if (!file) return true; 
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        return VALID_FILE_TYPES.includes(file.type) || 
               file.type.includes('pdf') || 
               file.type.includes('jpeg') || 
               file.type.includes('jpg') || 
               file.type.includes('png') || 
               (file.name && (
                 file.name.toLowerCase().endsWith('.pdf') || 
                 file.name.toLowerCase().endsWith('.jpg') || 
                 file.name.toLowerCase().endsWith('.jpeg') || 
                 file.name.toLowerCase().endsWith('.png')
               )) || 
               "Please upload only JPEG, PNG, or PDF files";
      }
    }
  }
};


export const entityDocUploadPartnershipValidation = {
  partnershipDeed: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  partnershipList: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  financialDocuments: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  yearToDateSales: {
    pattern: {
      value: /^\d+$/,
      message: "Please enter numbers only"
    },
    validate: {
      positive: (value) => {
        if (value && parseInt(value) <= 0) {
          return "Amount must be greater than 0";
        }
        return true;
      },
      maxLength: (value) => {
        if (value && value.length > 10) {
          return "Amount is too large";
        }
        return true;
      }
    }
  }
};

export const entityDocUploadProprietorshipValidation = {
  legalDocuments: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return (
          VALID_FILE_TYPES.includes(file.type) ||
          file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.type === "application/vnd.ms-excel" ||
          (file.name &&
            (file.name.toLowerCase().endsWith(".xlsx") ||
              file.name.toLowerCase().endsWith(".xls"))) ||
          "Please upload only JPEG, PNG, PDF, or Excel files"
        );
      }
    }
  },
  annualFinancials: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  totalSalesCurrentYear: {
    pattern: {
      value: /^\d+$/,
      message: "Please enter numbers only"
    },
    validate: {
      positive: (value) => {
        if (value && parseInt(value) <= 0) {
          return "Amount must be greater than 0";
        }
        return true;
      },
      maxLength: (value) => {
        if (value && value.length > 10) {
          return "Amount is too large";
        }
        return true;
      }
    }
  },
  proprietorPan: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  incorporationProof: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  itrFY: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  auditedBalanceFY: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  }
};
export const createKYCValidationRules = (hasPanNumber) => ({
  pan: {
    validate: {
      fileSize: validateFileSize,
      fileType: validateFileType
    }
  },
  aadhar: {
    validate: {
      fileSize: validateFileSize,
      fileType: validateFileType
    }
  }
});

export const promoterKYCDocValidation = {
  directors: {
    validate: {
      atLeastOne: (value) => value.length > 0 || "At least one director is required"
    }
  },
  "directors.*.pan": {
    validate: {
      fileSize: validateFileSize,
      fileType: validateFileType
    }
  },
  "directors.*.aadhar": {
    validate: {
      fileSize: validateFileSize,
      fileType: validateFileType
    }
  }
};

export const proprietorshipKYCDocValidation = {
  pan: {
    validate: {
      fileSize: validateFileSize,
      fileType: validateFileType
    }
  },
  aadhar: {
    validate: {
      fileSize: validateFileSize,
      fileType: validateFileType
    }
  }
};

export const bankDetailsDocValidation = {
  bankDetails: {
    required: "Bank details document is required",
    validate: {
      fileSize: (file) => {
        if (!file) return "Bank details document is required";
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size should not exceed 5MB";
      },
      fileType: (file) => {
        if (!file) return "Bank details document is required";
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  bankStatement: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || 
               file.type.includes('pdf') || 
               file.type.includes('jpeg') || 
               file.type.includes('jpg') || 
               file.type.includes('png') || 
               (file.name && (
                 file.name.toLowerCase().endsWith('.pdf') || 
                 file.name.toLowerCase().endsWith('.jpg') || 
                 file.name.toLowerCase().endsWith('.jpeg') || 
                 file.name.toLowerCase().endsWith('.png')
               )) || 
               "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  bankStatementOptional: {
    validate: {
      fileSize: (file) => {
        if (!file) return true; 
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || 
               file.type.includes('pdf') || 
               file.type.includes('jpeg') || 
               file.type.includes('jpg') || 
               file.type.includes('png') || 
               (file.name && (
                 file.name.toLowerCase().endsWith('.pdf') || 
                 file.name.toLowerCase().endsWith('.jpg') || 
                 file.name.toLowerCase().endsWith('.jpeg') || 
                 file.name.toLowerCase().endsWith('.png')
               )) || 
               "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  slLoanDetails: {
    validate: {
      fileSize: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (!file) return true;
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  }
};

export const requirementBriefValidation = {
  descriptionOfBusiness: {
    required: "Brief description of your business operations is required",
    minLength: {
      value: 50,
      message: "Please provide at least 50 characters"
    }
  },
  fundRequirement: {
    required: {
      value: true,
      message: "Fund requirement is required"
    }
  },
  endUseOfFund: {
    required: {
      value: true,
      message: "End use of fund is required"
    }
  },
  securityOffered: {
    required: "Security offered is required"
  },
  descriptionOfSecurity: {
    required: "Description is required when security is offered",
    minLength: {
      value: 20,
      message: "Please provide at least 20 characters"
    }
  }
};

export const individualBasicDetailsValidation = {
  panNumber: {
    required: "PAN is required",
    pattern: {
      value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
      message: "Please enter a valid PAN number"
    }
  },
  mobileNo: {
    required: "Mobile number is required",
    pattern: {
      value: /^(\+91-)?[0-9]{10}$/,
      message: "Please enter a valid mobile number"
    }
  },
  email: {
    required: "Email ID is required",
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: "Please enter a valid email address"
    }
  },
  profession: {
    required: "Profession is required"
  },
  confirmations: {
    required: "You must accept the terms and conditions"
  }
};

export const kycDocUploadValidation = {
  applicant: {
    required: "Applicant documents are required",
    validate: {
      fileSize: (file) => {
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  applicantIncomeTax: {
    required: "Income Tax Return is required",
    validate: {
      fileSize: (file) => {
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  applicantForm16: {
    validate: {
      fileSize: (file) => {
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  coApplicantPan: {
    required: "Co Applicant PAN is required",
    validate: {
      fileSize: (file) => {
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  coApplicantAadhar: {
    required: "Co Applicant Aadhar is required",
    validate: {
      fileSize: (file) => {
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  coApplicantIncomeTax: {
    required: "Co Applicant Income Tax Return is required",
    validate: {
      fileSize: (file) => {
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  },
  coApplicantForm16: {
    validate: {
      fileSize: (file) => {
        if (isRemoteFile(file)) return true;
        return file.size <= FILE_SIZE_LIMIT || "File size must be less than 5MB";
      },
      fileType: (file) => {
        if (isRemoteFile(file)) return true;
        return VALID_FILE_TYPES.includes(file.type) || "Please upload only JPEG, PNG, or PDF files";
      }
    }
  }
};

export const salarySlipsValidation = {
  validate: {
    fileCount: (files) => {
      if (!files || files.length === 0) {
        return true;
      }
      if (files.length > 0 && files.length < 3) {
        return "Please upload exactly 3 salary slips or none at all";
      }
      return true;
    }
  }
};